import { useQuery } from "@apollo/client/react";

import { useCacheAndNetworkOnce } from "src/apollo/useCacheAndNetworkOnce";
import { useOrgFeatureFlagsForTenant } from "src/hooks/orgConfig";
import type { Design } from "src/pages/Designs/designUtils";
import { listDesignWorkspaceIds as listDesignWorkspaceIdsQuery } from "src/pages/Designs/designs.graphql";

type ListDesignWorkspaceIdsData = {
  designs: Pick<Design, "id" | "designWorkspaceId">[];
};

export type IsDesignWorkspaceResult = {
  isDesignWorkspace: boolean;
  loading: boolean;
};

/**
 * Determines whether a workspace was provisioned for a design, i.e. it is
 * referenced as the `designWorkspaceId` of any design. Design workspaces have
 * their access managed by the design, so the access config is read-only.
 *
 * Only queries when the designs feature is enabled for the tenant.
 */
const useIsDesignWorkspace = (
  workspaceId: string | null | undefined
): IsDesignWorkspaceResult => {
  const { featureFlags, loading: orgConfigLoading } =
    useOrgFeatureFlagsForTenant();
  const enableDesigns = featureFlags?.designs?.enableDesigns ?? false;

  const skip = !enableDesigns || !workspaceId;
  const fetchPolicy = useCacheAndNetworkOnce("listDesignWorkspaceIds", {
    enabled: !skip,
  });
  const { data, loading } = useQuery<ListDesignWorkspaceIdsData>(
    listDesignWorkspaceIdsQuery,
    {
      skip,
      ...fetchPolicy,
    }
  );

  if (!workspaceId) {
    return { isDesignWorkspace: false, loading: false };
  }

  return {
    isDesignWorkspace: (data?.designs ?? []).some(
      (design) => design.designWorkspaceId === workspaceId
    ),
    // The query is skipped (loading false) until the tenant flags arrive, so
    // count that wait as loading too - otherwise callers read a premature "no".
    loading: orgConfigLoading || loading,
  };
};

export default useIsDesignWorkspace;
